import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/api';

/**
 * Client data.
 *
 * One request for the list, one for a client's whole form (details, contacts
 * and rates together), and one cached request for the reference lists. The
 * legacy page issued at least six queries to render the same form, several of
 * them `SELECT *` from the same row.
 */

export interface Client {
    readonly id: number;
    readonly companyName: string;
    readonly xeroContactId: string | null;
    readonly firstName: string | null;
    readonly lastName: string | null;
    readonly emailAddress: string | null;
    readonly phoneNumber: string | null;
    readonly directPhoneNumber: string | null;
    readonly streetName: string | null;
    readonly suburbName: string | null;
    readonly postcode: string | null;
    readonly state: string | null;
    readonly country: string | null;
    readonly abn: string | null;
    readonly xeroDueDays: number | null;
    readonly xeroDueId: number | null;
    readonly xeroAmountsAreId: number | null;
    readonly xeroSalesGstId: number | null;
    readonly xeroSalesAccount: string | null;
    readonly isActive: boolean;
    readonly createdAt: string;
}

export interface ClientContact {
    readonly id: number;
    readonly clientId: number;
    readonly contactName: string;
    readonly jobTitle: string | null;
    readonly division: string | null;
    readonly phoneNumber: string | null;
    readonly phoneNumber2: string | null;
    readonly emailAddress: string | null;
}

export interface ClientRate {
    readonly id: number;
    readonly clientId: number;
    readonly staffTitleId: number;
    readonly staffTitleName: string | null;
    readonly ratePerHour: number;
    readonly isActive: boolean;
}

export interface ClientDetail {
    readonly client: Client;
    readonly contacts: readonly ClientContact[];
    readonly rates: readonly ClientRate[];
}

export interface XeroOption {
    readonly id: number;
    readonly kind: string;
    readonly label: string;
    readonly xeroCode: string;
}

export interface Skillset {
    readonly id: number;
    readonly title: string;
    readonly colour: string | null;
    readonly hireRate: number | null;
}

interface Reference {
    readonly xeroOptions: readonly XeroOption[];
    readonly skillsets: readonly Skillset[];
}

/** Picklists do not change while someone edits a client, so they load once. */
let referenceCache: Reference | null = null;

export function useClientReference(): Reference {
    const [reference, setReference] = useState<Reference>(
        referenceCache ?? { xeroOptions: [], skillsets: [] },
    );

    useEffect(() => {
        if (referenceCache !== null) {
            return;
        }
        let cancelled = false;
        Promise.all([
            apiFetch<{ options: readonly XeroOption[] }>('/clients/xero-options'),
            apiFetch<{ skillsets: readonly Skillset[] }>('/skillsets'),
        ])
            .then(([o, s]) => {
                referenceCache = { xeroOptions: o.options, skillsets: s.skillsets };
                if (!cancelled) {
                    setReference(referenceCache);
                }
            })
            .catch(() => {
                /* The form still works without picklists; the selects are just empty. */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return reference;
}

interface ListState {
    readonly clients: readonly Client[];
    readonly loading: boolean;
    readonly error: string | null;
    reload: () => void;
}

export function useClientList(search: string, includeInactive: boolean): ListState {
    const [clients, setClients] = useState<readonly Client[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [nonce, setNonce] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        // Typing in the search box should not fire a request per keystroke.
        const timer = setTimeout(() => {
            const params = new URLSearchParams();
            if (search.trim() !== '') {
                params.set('search', search.trim());
            }
            if (includeInactive) {
                params.set('includeInactive', '1');
            }
            apiFetch<{ clients: readonly Client[] }>(`/clients?${params.toString()}`)
                .then((r) => {
                    if (!cancelled) {
                        setClients(r.clients);
                        setError(null);
                    }
                })
                .catch((err: unknown) => {
                    if (!cancelled) {
                        setError(err instanceof Error ? err.message : 'Could not load clients');
                    }
                })
                .finally(() => {
                    if (!cancelled) {
                        setLoading(false);
                    }
                });
        }, search.trim() === '' ? 0 : 250);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [search, includeInactive, nonce]);

    const reload = useCallback(() => {
        setNonce((n) => n + 1);
    }, []);

    return { clients, loading, error, reload };
}

interface DetailState {
    readonly detail: ClientDetail | null;
    readonly loading: boolean;
    readonly error: string | null;
    reload: () => void;
}

export function useClientDetail(id: number | null): DetailState {
    const [detail, setDetail] = useState<ClientDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [nonce, setNonce] = useState(0);

    useEffect(() => {
        if (id === null) {
            setDetail(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        apiFetch<ClientDetail>(`/clients/${String(id)}`)
            .then((r) => {
                if (!cancelled) {
                    setDetail(r);
                    setError(null);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Could not load the client');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [id, nonce]);

    const reload = useCallback(() => {
        setNonce((n) => n + 1);
    }, []);

    return { detail, loading, error, reload };
}

/** Xero picklists split by kind, which is how the Xero tab renders them. */
export function useXeroOptionsByKind(options: readonly XeroOption[]): Record<string, XeroOption[]> {
    return useMemo(() => {
        const out: Record<string, XeroOption[]> = {};
        for (const o of options) {
            (out[o.kind] ??= []).push(o);
        }
        return out;
    }, [options]);
}
